import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { setContext } from '@sentry/react';

import styles from './PricingPlans.module.scss';

import {
  defaultError,
  invalidParametersError,
  missingOrgError,
  paddleProductsError
} from '../../config/errors.config';
import { APP_TITLE } from '../../constants/app';
import { SENTRY_SERVICE_CONTEXT } from '../../constants/sentry';
import { getService } from '../../services';
import { ServiceError } from '../../services/errors';
import { getPaddleProductPrices } from '../../services/paddle';
import { usePricingPlansStore } from '../../stores/usePricingPlansStore';
import { ErrorCard } from '../../stories/ErrorCard/ErrorCard';
import { PricingCard } from '../../stories/PricingCard/PricingCard';
import { PricingPlansLoadingCard } from '../../stories/PricingPlansLoadingCard/PricingPlansLoadingCard';
import { Plan, PricingParams } from '../../types/pricing.types';
import { getUrlParams } from '../../util/params';
import { pricingPlanSchema } from '../../validations/pricingPlans';

export default function PricingPlans() {
  const {
    setParams,
    params,
    setOrganization,
    organization,
    paddleProducts,
    setPaddleProducts
  } = usePricingPlansStore();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ServiceError>();

  useEffect(() => {
    const queryParams = getUrlParams<PricingParams>(searchParams);

    if (Object.keys(queryParams).length === 0 && organization) {
      setLoading(false);

      return;
    }

    const parsed = pricingPlanSchema.safeParse(queryParams);
    if (parsed.success) {
      setParams({ params: parsed.data });
    } else {
      setError(
        new ServiceError(
          invalidParametersError.title,
          invalidParametersError.message
        )
      );
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (params) setup(params);
  }, [params]);

  const setup = async (parameters: PricingParams) => {
    try {
      setContext(SENTRY_SERVICE_CONTEXT, {
        userId: parameters.user,
        host: parameters.host,
        org: parameters.org,
        service: parameters.service,
        ide: parameters.ide,
        scheme: parameters.scheme,
        pat: parameters.pat
      });

      const service = getService(
        parameters.service,
        parameters.host,
        parameters.pat === 'true',
        parameters.username
      );

      const organization = await service.getOrganization(
        parameters.token,
        parameters.org
      );
      setOrganization(organization);

      const paddlePrices = await getPaddleProductPrices();
      setPaddleProducts(paddlePrices);

      navigate('/plans');
    } catch (error: unknown) {
      console.error(error);
      if (error instanceof ServiceError) {
        setError(error);
      } else {
        setError(new ServiceError(defaultError.title, defaultError.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const generatePaymentLink = (plan: Plan, billedAnnually: boolean): string => {
    if (!organization) {
      throw new ServiceError(missingOrgError.title, missingOrgError.message);
    }

    if (!params) {
      throw new ServiceError(
        invalidParametersError.title,
        invalidParametersError.message
      );
    }

    const payload = {
      plan,
      billing: billedAnnually ? 'annually' : 'monthly',
      userId: params.user,
      username: params.username,
      owner: organization,
      service: params.service,
      host: params.host,
      org: params.org
    };

    return `${window.location.origin}/payment?token=${btoa(JSON.stringify(payload))}`;
  };

  if (loading) {
    return (
      <section className={styles.container}>
        <Helmet>
          <title>{APP_TITLE} - Pricing Plans</title>
        </Helmet>
        <PricingPlansLoadingCard />
      </section>
    );
  }

  if (error || !organization) {
    return (
      <section className={styles.container}>
        <Helmet>
          <title>{APP_TITLE} - Pricing Plans</title>
        </Helmet>
        <ErrorCard
          title={error?.name ?? missingOrgError.title}
          message={error?.message ?? invalidParametersError.message}
        />
      </section>
    );
  }

  if (
    !paddleProducts ||
    !paddleProducts[Plan.Personal] ||
    !paddleProducts[Plan.Team] ||
    !paddleProducts[Plan.Enterprise]
  ) {
    return (
      <section className={styles.container}>
        <Helmet>
          <title>{APP_TITLE} - Pricing Plans</title>
        </Helmet>
        <ErrorCard
          title={paddleProductsError.title}
          message={paddleProductsError.message}
        />
      </section>
    );
  }

  return (
    <section className={styles.container}>
      <Helmet>
        <title>{APP_TITLE} - Pricing Plans</title>
      </Helmet>
      <PricingCard
        enterprise={organization.enterprise}
        products={paddleProducts}
        generatePaymentLink={generatePaymentLink}
      />
    </section>
  );
}
